import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';

/**
 * WizardStepper
 *
 * Renders the step indicator bar. Step labels and count change dynamically
 * based on which path the user chose in Step 2 (referenceDecision).
 *
 * Path A  referenceDecision === true      → 5 steps, Step 3 = "Select Template"
 * Path B  referenceDecision === false     → 5 steps, Step 3 = "Select Baseline"
 * Path C  referenceDecision === 'scratch' → 4 steps, Step 3 is skipped entirely
 *
 * Props:
 *   currentStep        — raw internal step number (1–5)
 *   referenceDecision  — true | false | 'scratch' | null
 *   onGoToStep(n)      — called with the internal step number (not display step)
 */

// ─── Step definitions per path ────────────────────────────────────────────────

const STEPS_PATH_A = [
  { number: 1, label: 'Details'         },
  { number: 2, label: 'Reference'       },
  { number: 3, label: 'Past Work'       },
  { number: 4, label: 'Build Bid'       },
  { number: 5, label: 'Build Proposal'        },
];

const STEPS_PATH_B = [
  { number: 1, label: 'Details'         },
  { number: 2, label: 'Reference'       },
  { number: 3, label: 'Baseline'        },
  { number: 4, label: 'Build Bid'       },
  { number: 5, label: 'Build Proposal'        },
];

// Path C: only 4 display steps. Internal step 4 maps to display step 3,
// internal step 5 maps to display step 4.
const STEPS_PATH_C = [
  { number: 1, internalStep: 1, label: 'Details'   },
  { number: 2, internalStep: 2, label: 'Reference' },
  { number: 3, internalStep: 4, label: 'Build Bid' },
  { number: 4, internalStep: 5, label: 'Build Proposal'  },
];

// Before user picks a path, show generic 5-step layout
const STEPS_DEFAULT = [
  { number: 1, label: 'Details'    },
  { number: 2, label: 'Reference'  },
  { number: 3, label: 'Template'   },
  { number: 4, label: 'Build Bid'  },
  { number: 5, label: 'Build Proposal'   },
];

const getSteps = (referenceDecision) => {
  if (referenceDecision === true)       return STEPS_PATH_A;
  if (referenceDecision === false)      return STEPS_PATH_B;
  if (referenceDecision === 'scratch')  return STEPS_PATH_C;
  return STEPS_DEFAULT;
};

/**
 * Maps the raw internal currentStep → the display step index for Path C.
 * Steps 1 & 2 are unchanged. Internal step 4 is display step 3.
 * Internal step 5 is display step 4.
 */
const toDisplayStep = (currentStep, referenceDecision) => {
  if (referenceDecision !== 'scratch') return currentStep;
  if (currentStep === 5) return 4;
  if (currentStep === 4) return 3;
  return currentStep;
};

// ─── Component ────────────────────────────────────────────────────────────────

const WizardStepper = ({ currentStep, referenceDecision, onGoToStep }) => {
  const steps       = getSteps(referenceDecision);
  const displayStep = toDisplayStep(currentStep, referenceDecision);
  const isPathC     = referenceDecision === 'scratch';

  return (
    <div className="flex items-center justify-between max-w-3xl mx-auto">
      {steps.map((step, idx) => {
        // For Path C, each step has an internalStep field; for others, number === internalStep
        const internalStep  = step.internalStep ?? step.number;
        const displayNumber = step.number;

        const isCompleted = displayNumber < displayStep;
        const isCurrent   = displayNumber === displayStep;
        const isFuture    = displayNumber > displayStep;

        // Allow navigating to completed steps or the current step
        const isClickable = displayNumber <= displayStep;

        return (
          <React.Fragment key={displayNumber}>
            <div className="flex flex-col items-center">
              <button
                onClick={() => isClickable && onGoToStep(internalStep)}
                disabled={isFuture}
                className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all
                  ${isCompleted
                    ? 'bg-teal-500 text-white'
                    : isCurrent
                      ? 'bg-brightblue-500 text-white ring-4 ring-brightblue-200'
                      : 'bg-grey-200 text-grey-600'
                  }
                  ${isClickable ? 'cursor-pointer hover:scale-110' : 'cursor-not-allowed'}
                `}
              >
                {isCompleted ? <CheckIcon className="w-6 h-6" /> : displayNumber}
              </button>

              <div className={`mt-2 text-xs font-medium ${
                isCurrent ? 'text-darkblue-600' : 'text-grey-600'
              }`}>
                {step.label}
              </div>
            </div>

            {/* Connector line between steps */}
            {idx < steps.length - 1 && (
              <div
                className={`flex-1 h-1 mx-4 rounded-full transition-colors ${
                  displayNumber < displayStep ? 'bg-teal-500' : 'bg-grey-300'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default WizardStepper;